/**
 * Game Logger Service
 *
 * Circular buffer logger for tracking game events.
 * Stores the last 50 events with timestamps for debugging purposes.
 */

import { Capacitor } from '@capacitor/core';

const MAX_LOG_SIZE = 50;

class GameLogger {
    constructor() {
        this.logs = [];
        this.isEnabled = true;
    }

    /**
     * Log a game event
     * 
     * @param {string} event - Event name/type
     * @param {Object} data - Event data (will be serialized)
     */
    log(event, data = {}) {
        if (!this.isEnabled) return;

        const entry = {
            timestamp: new Date().toISOString(),
            event,
            data: this._sanitizeData(data),
        };

        this.logs.push(entry);

        // Maintain circular buffer - remove oldest entries if over limit
        if (this.logs.length > MAX_LOG_SIZE) {
            this.logs = this.logs.slice(-MAX_LOG_SIZE);
        }

        // Also log to console:
        // - Mobile (Capacitor): Always log (for Sentry and debugging)
        // - Web: Only if REACT_APP_DEBUG_MODE is true
        const shouldLog = Capacitor.isNativePlatform() || process.env.REACT_APP_DEBUG_MODE === 'true';
        if (shouldLog) {
            console.log(`[GameLogger] ${event}`, data);
        }
    }

    /**
     * Get all logged events
     * 
     * @returns {Array} Array of log entries
     */
    getLogs() {
        return [...this.logs];
    }

    /**
     * Get logs as a formatted string (for display)
     * 
     * @returns {string} Formatted log string
     */
    getLogsAsString() {
        return this.logs.map(entry =>
            `[${entry.timestamp}] ${entry.event}: ${JSON.stringify(entry.data)}`
        ).join('\n');
    }

    /**
     * Clear all logs
     */
    clear() {
        this.logs = [];
    }

    /**
     * Enable/disable logging
     * 
     * @param {boolean} enabled 
     */
    setEnabled(enabled) {
        this.isEnabled = enabled;
    }

    /**
     * Sanitize data to ensure it's serializable and doesn't contain sensitive info
     * 
     * @param {Object} data 
     * @returns {Object} Sanitized data
     */
    _sanitizeData(data) {
        try {
            // Attempt to serialize and parse to ensure it's JSON-safe
            const serialized = JSON.stringify(data, (key, value) => {
                // Skip functions
                if (typeof value === 'function') return '[Function]';
                // Skip DOM elements
                if (value instanceof HTMLElement) return '[HTMLElement]';
                // Skip circular references (handled by JSON.stringify)
                // Limit string length
                if (typeof value === 'string' && value.length > 500) {
                    return value.substring(0, 500) + '...[truncated]';
                }
                return value;
            });
            return JSON.parse(serialized);
        } catch (error) {
            return { error: 'Failed to serialize data', originalType: typeof data };
        }
    }
}

// Export singleton instance
const gameLogger = new GameLogger();

// Also export the class for testing
export { GameLogger };
export default gameLogger;
